import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { CheckCircle, Loader2, Calendar, Mail, ArrowRight, Home } from 'lucide-react'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

export default function PaymentSuccess({ onNavigate }) {
  const [status, setStatus] = useState('loading') // loading | confirmed | error
  const [form, setForm] = useState({ name: '', email: '', details: '' })
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const sessionId = params.get('session_id')
    if (!sessionId) { setStatus('error'); return }

    // Poll for payment confirmation
    let attempts = 0
    const poll = async () => {
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`)
        if (!res.ok) { setStatus('error'); return }
        const data = await res.json()
        if (data.payment_status === 'paid') {
          setStatus('confirmed')
        } else if (attempts < 8) {
          attempts++
          setTimeout(poll, 1500)
        } else {
          // Still pending — show partial confirmation
          setStatus('confirmed')
        }
      } catch {
        setStatus('error')
      }
    }
    poll()
  }, [])

  const handleSubmitDetails = async (e) => {
    e.preventDefault()
    const params = new URLSearchParams(window.location.search)
    const sessionId = params.get('session_id')
    try {
      await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message: `CONSULTATION BOOKING (Session: ${sessionId})\n\nAvailability / Details:\n${form.details}`,
        }),
      })
      setSubmitted(true)
    } catch {}
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4 py-16">
      <div className="max-w-lg w-full">

        {status === 'loading' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="text-center space-y-4">
            <Loader2 size={32} className="animate-spin text-[#0047AB] mx-auto" />
            <p className="font-body text-[#6B7280]">Confirming your payment…</p>
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-4">
            <div className="text-4xl">⚠️</div>
            <h2 className="font-sans font-bold text-2xl text-[#111111]">Something went wrong</h2>
            <p className="font-body text-[#6B7280]">
              Your payment may have gone through. Email <a href="mailto:akshayraoisme@gmail.com"
              className="text-[#0047AB] underline">akshayraoisme@gmail.com</a> and we'll sort it.
            </p>
            <button onClick={() => onNavigate && onNavigate('hero')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#111111] text-white text-sm rounded-xl hover:bg-[#0047AB] transition-colors">
              <Home size={14} /> Back to site
            </button>
          </motion.div>
        )}

        {status === 'confirmed' && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6">

            {/* Success card */}
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-card text-center">
              <div className="w-14 h-14 bg-emerald-50 border border-emerald-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={28} className="text-emerald-600" />
              </div>
              <h1 className="font-sans font-bold text-2xl text-[#111111] mb-2">Booking confirmed!</h1>
              <p className="font-body text-[#6B7280] text-sm leading-relaxed">
                Your $8.99 consultation has been paid. Fill in your availability below and Akshay will confirm a time within 24 hours.
              </p>
            </div>

            {/* Booking details form */}
            {!submitted ? (
              <div className="bg-white border border-[#E5E7EB] rounded-2xl p-7 shadow-card">
                <div className="flex items-center gap-2 mb-5">
                  <Calendar size={16} className="text-[#0047AB]" />
                  <h2 className="font-sans font-semibold text-[#111111] text-base">Complete your booking</h2>
                </div>
                <form onSubmit={handleSubmitDetails} className="space-y-4">
                  <BookingField label="Your Name" name="name" type="text" value={form.name}
                    onChange={e => setForm(p => ({...p, name: e.target.value}))}
                    placeholder="Full name" required />
                  <BookingField label="Your Email" name="email" type="email" value={form.email}
                    onChange={e => setForm(p => ({...p, email: e.target.value}))}
                    placeholder="your@email.com" required />
                  <BookingField label="Your availability + what you'd like to discuss" name="details"
                    type="textarea" value={form.details}
                    onChange={e => setForm(p => ({...p, details: e.target.value}))}
                    placeholder="e.g. Available Mon–Wed afternoons AEST. Want to discuss setting up a business website and email for my new cafe."
                    rows={4} required />
                  <button type="submit"
                    className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#0047AB] text-white text-sm font-semibold rounded-xl hover:bg-[#003d94] transition-colors">
                    Send booking details <ArrowRight size={14} />
                  </button>
                </form>
              </div>
            ) : (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="bg-white border border-emerald-200 rounded-2xl p-7 text-center shadow-card">
                <Mail size={24} className="text-emerald-600 mx-auto mb-3" />
                <h2 className="font-sans font-semibold text-[#111111] mb-1">All set.</h2>
                <p className="font-body text-[#6B7280] text-sm">
                  Akshay will be in touch within 24 hours to confirm your session time.
                </p>
                <button onClick={() => onNavigate && onNavigate('hero')}
                  className="mt-4 inline-flex items-center gap-2 text-sm text-[#0047AB] font-mono hover:underline">
                  <Home size={13} /> Back to site
                </button>
              </motion.div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  )
}

function BookingField({ label, name, type, value, onChange, placeholder, rows, required }) {
  const base = "w-full bg-[#F9F9F9] border border-[#E5E7EB] rounded-xl px-4 py-3 text-[#111111] text-sm font-body placeholder:text-[#C4C9D4] focus:outline-none focus:border-[#0047AB]/60 focus:ring-2 focus:ring-[#0047AB]/10 transition-all"
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-2">{label}</label>
      {type === 'textarea'
        ? <textarea name={name} value={value} onChange={onChange} placeholder={placeholder}
            rows={rows} required={required} className={`${base} resize-none`} />
        : <input type={type} name={name} value={value} onChange={onChange}
            placeholder={placeholder} required={required} className={base} />}
    </div>
  )
}
